import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { erpApi } from '@/shared/axios/erp-api';
import {
  getUrlParamsFetch,
  notFoundIdHandler,
  urlBackend,
} from '@/shared';
import {
  EstadoLlamada,
  EstadosLlamadaPagingRes,
  UseFetchEnabledParams,
  UseMutationsParams,
} from '@/shared/interfaces';

import { useUiStore } from '@/store/ui';

const { get, post, put, remove } = erpApi();

///* tanStack query
export const useFetchEstadoLlamadas = ({
  enabled = true,
  params,
}: UseFetchEnabledParams<GetEstadoLlamadasParams>) => {
  return useQuery({
    queryKey: ['estado-llamadas', ...Object.values(params || {})],
    queryFn: () => getEstadoLlamadas(params),
    enabled: enabled,
  });
};

export const useGetEstadoLlamada = (id: string) => {
  return useQuery({
    queryKey: ['estado-llamada', id],
    queryFn: () => getEstadoLlamada(id),
    retry: false,
  });
};

export const useCreateEstadoLlamada = ({
  navigate,
  returnUrl,
  enableRedirect = true,
}: UseMutationsParams) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: createEstadoLlamada,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['estado-llamadas'] });
      enableRedirect && navigate && returnUrl && navigate(returnUrl);
    },
  });
};

export const useUpdateEstadoLlamada = ({
  navigate,
  returnUrl,
  enableRedirect = true,
}: UseMutationsParams) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: updateEstadoLlamada,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['estado-llamadas'] });
      enableRedirect && navigate && returnUrl && navigate(returnUrl);
    },
  });
};

export const useDeleteEstadoLlamada = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: deleteEstadoLlamada,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['estado-llamadas'] });
    },
  });
};

///* axios
export type GetEstadoLlamadasParams = {
  page?: number;
  page_size?: number;

  // filters
  nombre?: string;
  codigo?: string;
};
export type CreateEstadoLlamadaParams = Omit<
  EstadoLlamada,
  'id_estado_contrato'
>;
export type UpdateEstadoLlamadaParams = {
  id: string;
  data: CreateEstadoLlamadaParams;
};

export const getEstadoLlamadas = async (params?: GetEstadoLlamadasParams) => {
  const queryParams = getUrlParamsFetch(params || {});

  return get<EstadosLlamadaPagingRes>(
    `${urlBackend}api/v1/estados-llamada/?${queryParams}`,
    true
  );
};

export const getEstadoLlamada = async (id: string) => {
  const res = await get<EstadoLlamada>(
    `${urlBackend}api/v1/estados-llamada/${id}`,
    true
  );

  notFoundIdHandler(res);
  return res;
};

export const createEstadoLlamada = async (data: CreateEstadoLlamadaParams) => {
  const setAlert = useUiStore.getState().setAlert;

  const res = await post<EstadoLlamada>(
    `${urlBackend}api/v1/estados-llamada/`,
    data,
    true
  );

  setAlert(true, res.message, res.status);
  return res;
};

export const updateEstadoLlamada = async ({
  id,
  data,
}: UpdateEstadoLlamadaParams) => {
  const setAlert = useUiStore.getState().setAlert;

  const res = await put<EstadoLlamada>(
    `${urlBackend}api/v1/estados-llamada/${id}/`,
    data,
    true
  );

  setAlert(true, res.message, res.status);
  return res;
};

export const deleteEstadoLlamada = async (id: string) => {
  const setAlert = useUiStore.getState().setAlert;

  const res = await remove<EstadoLlamada>(
    `${urlBackend}api/v1/estados-llamada/${id}/`,
    true
  );

  setAlert(true, res.message, res.status);
  return res;
};
